import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useFamilyContext } from './FamilyContext';
import { supabase } from '../lib/supabase';

const MalcolmContext = createContext();

export const useMalcolmContext = () => {
  const context = useContext(MalcolmContext);
  if (!context) {
    throw new Error('useMalcolmContext must be used within a MalcolmProvider');
  }
  return context;
};

export const MalcolmProvider = ({ children }) => {
  const { user, currentUser } = useFamilyContext();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // AI Intel State
  const [feedItems, setFeedItems] = useState([]);
  const [feedSources, setFeedSources] = useState([]);
  const [insights, setInsights] = useState([]);
  const [savedItems, setSavedItems] = useState([]);
  const [skills, setSkills] = useState([]);
  const [betaOpportunities, setBetaOpportunities] = useState([]);
  const [companyIntelligence, setCompanyIntelligence] = useState([]);
  const [modelComparisons, setModelComparisons] = useState([]);
  
  // Filter and view state
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('relevance'); // 'relevance', 'date', 'trending'
  
  // Local storage keys
  const STORAGE_KEYS = {
    FEED_ITEMS: 'malcolm_ai_feed_items',
    SAVED_ITEMS: 'malcolm_saved_items',
    INSIGHTS: 'malcolm_ai_insights',
    PREFERENCES: 'malcolm_feed_preferences'
  };

  // Check if current user is Malcolm
  const isMalcolm = currentUser?.name === 'Malcolm' || currentUser?.id === 'parent2';

  // Load feed sources
  const loadFeedSources = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('ai_feed_sources')
        .select('*')
        .eq('active', true)
        .order('company', { ascending: true });

      if (error) throw error;
      setFeedSources(data || []);
    } catch (err) {
      console.error('Error loading feed sources:', err);
      setError(err.message);
    }
  }, []);

  // Load feed items with filters
  const loadFeedItems = useCallback(async (filter = activeFilter) => {
    try {
      let query = supabase
        .from('ai_feed_items')
        .select(`
          *,
          source:ai_feed_sources(*)
        `)
        .order(sortBy === 'date' ? 'published_at' : sortBy === 'trending' ? 'trending_score' : 'relevance_score', { ascending: false })
        .limit(50);

      // Apply filters
      if (filter === 'high_relevance') {
        query = query.gte('relevance_score', 80);
      } else if (filter === 'trending') {
        query = query.gte('trending_score', 70);
      } else if (filter === 'beta') {
        query = query.contains('tags', ['beta_access']);
      }

      // Apply search
      if (searchQuery) {
        query = query.or(`title.ilike.%${searchQuery}%,summary.ilike.%${searchQuery}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      
      setFeedItems(data || []);
      // Cache to localStorage
      localStorage.setItem(STORAGE_KEYS.FEED_ITEMS, JSON.stringify(data || []));
    } catch (err) {
      console.error('Error loading feed items:', err);
      setError(err.message);
    }
  }, [activeFilter, searchQuery, sortBy]);

  // Load AI insights
  const loadInsights = useCallback(async () => {
    if (!isMalcolm || !currentUser?.id) return;

    try {
      const { data, error } = await supabase
        .from('ai_analysis_insights')
        .select('*')
        .eq('user_id', currentUser.id)
        .eq('is_dismissed', false)
        .gte('expires_at', new Date().toISOString())
        .order('generated_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      setInsights(data || []);
      localStorage.setItem(STORAGE_KEYS.INSIGHTS, JSON.stringify(data || []));
    } catch (err) {
      console.error('Error loading insights:', err);
    }
  }, [currentUser?.id, isMalcolm]);

  // Load saved items
  const loadSavedItems = useCallback(async () => {
    if (!isMalcolm || !currentUser?.id) return;

    try {
      const { data, error } = await supabase
        .from('ai_research_workspace')
        .select(`
          *,
          item:ai_feed_items(*, source:ai_feed_sources(*))
        `)
        .eq('user_id', currentUser.id)
        .eq('is_archived', false)
        .order('saved_at', { ascending: false });

      if (error) throw error;
      setSavedItems(data || []);
      localStorage.setItem(STORAGE_KEYS.SAVED_ITEMS, JSON.stringify(data || []));
    } catch (err) {
      console.error('Error loading saved items:', err);
    }
  }, [currentUser?.id, isMalcolm]);

  // Load skills
  const loadSkills = useCallback(async () => {
    if (!isMalcolm || !currentUser?.id) return;

    try {
      const { data, error } = await supabase
        .from('ai_skill_tracking')
        .select('*')
        .eq('user_id', currentUser.id)
        .order('skill_name', { ascending: true });

      if (error) throw error;
      setSkills(data || []);
    } catch (err) {
      console.error('Error loading skills:', err);
    }
  }, [currentUser?.id, isMalcolm]);

  // Load beta opportunities
  const loadBetaOpportunities = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('ai_beta_opportunities')
        .select('*')
        .eq('status', 'open')
        .order('deadline', { ascending: true });

      if (error) throw error;
      setBetaOpportunities(data || []);
    } catch (err) {
      console.error('Error loading beta opportunities:', err);
    }
  }, []);

  // Load company intelligence
  const loadCompanyIntelligence = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('ai_company_intelligence')
        .select('*')
        .order('company_name', { ascending: true });

      if (error) throw error;
      setCompanyIntelligence(data || []);
    } catch (err) {
      console.error('Error loading company intelligence:', err);
    }
  }, []);

  // Load model comparisons
  const loadModelComparisons = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('model_comparisons')
        .select('*')
        .order('last_updated', { ascending: false })
        .limit(1);

      if (error) throw error;
      setModelComparisons(data || []);
    } catch (err) {
      console.error('Error loading model comparisons:', err);
    }
  }, []);

  // Save item to workspace
  const saveToWorkspace = async (itemId, category, notes = '') => {
    if (!isMalcolm || !currentUser?.id) return;

    try {
      const { data, error } = await supabase
        .from('ai_research_workspace')
        .insert({
          user_id: currentUser.id,
          item_id: itemId,
          category,
          notes
        })
        .select()
        .single();

      if (error) throw error;
      
      // Reload saved items
      await loadSavedItems();
      return data;
    } catch (err) {
      console.error('Error saving to workspace:', err);
      throw err;
    }
  };

  // Update skill level
  const updateSkillLevel = async (skillId, newLevel) => {
    if (!isMalcolm || !currentUser?.id) return;

    try {
      const { error } = await supabase
        .from('ai_skill_tracking')
        .update({
          current_level: newLevel,
          last_updated: new Date().toISOString()
        })
        .eq('id', skillId)
        .eq('user_id', currentUser.id);

      if (error) throw error;
      
      // Reload skills
      await loadSkills();
    } catch (err) {
      console.error('Error updating skill:', err);
      throw err;
    }
  };

  // Dismiss insight
  const dismissInsight = async (insightId) => {
    if (!isMalcolm || !currentUser?.id) return;

    try {
      const { error } = await supabase
        .from('ai_analysis_insights')
        .update({ is_dismissed: true })
        .eq('id', insightId)
        .eq('user_id', currentUser.id);

      if (error) throw error;
      
      // Remove from local state
      setInsights(prev => prev.filter(i => i.id !== insightId));
    } catch (err) {
      console.error('Error dismissing insight:', err);
      throw err;
    }
  };

  // Delete feed item
  const deleteFeedItem = async (itemId) => {
    try {
      const { error } = await supabase
        .from('ai_feed_items')
        .delete()
        .eq('id', itemId);

      if (error) throw error;
      
      // Remove from local state
      setFeedItems(prev => prev.filter(item => item.id !== itemId));
      
      // Update localStorage
      const updatedItems = feedItems.filter(item => item.id !== itemId);
      localStorage.setItem(STORAGE_KEYS.FEED_ITEMS, JSON.stringify(updatedItems));
      
      return true;
    } catch (err) {
      console.error('Error deleting feed item:', err);
      throw err;
    }
  };

  // Initial data load - simplified for speed
  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    // Show cached data immediately and stop loading
    const cachedFeedItems = localStorage.getItem(STORAGE_KEYS.FEED_ITEMS);
    if (cachedFeedItems) {
      setFeedItems(JSON.parse(cachedFeedItems));
    }
    
    // Always stop loading quickly
    setLoading(false);
    
    // Load fresh data in background (don't wait for it)
    const loadBackgroundData = async () => {
      try {
        const { data: feedData } = await supabase
          .from('ai_feed_items')
          .select(`*, source:ai_feed_sources(*)`)
          .order('relevance_score', { ascending: false })
          .limit(50);
        
        if (feedData) {
          setFeedItems(feedData);
          localStorage.setItem(STORAGE_KEYS.FEED_ITEMS, JSON.stringify(feedData));
        }
      } catch (err) {
        console.error('Background data load error:', err);
      }
    };
    
    // Start background load but don't wait for it
    loadBackgroundData();
  }, [user]);

  // Real-time subscriptions
  useEffect(() => {
    if (!user) return;

    // Subscribe to feed items updates
    const feedSubscription = supabase
      .channel('ai_feed_updates')
      .on('postgres_changes', 
        { event: 'INSERT', schema: 'public', table: 'ai_feed_items' },
        (payload) => {
          // Add new item to the top if it matches filters
          setFeedItems(prev => [payload.new, ...prev]);
        }
      )
      .subscribe();

    // Subscribe to insights updates (only for Malcolm)
    let insightsSubscription;
    if (isMalcolm && currentUser?.id) {
      insightsSubscription = supabase
        .channel('ai_insights_updates')
        .on('postgres_changes', 
          { 
            event: 'INSERT', 
            schema: 'public', 
            table: 'ai_analysis_insights',
            filter: `user_id=eq.${currentUser.id}`
          },
          (payload) => {
            setInsights(prev => [payload.new, ...prev]);
          }
        )
        .subscribe();
    }

    return () => {
      feedSubscription.unsubscribe();
      if (insightsSubscription) insightsSubscription.unsubscribe();
    };
  }, [user, currentUser?.id, isMalcolm]);

  const value = {
    // State
    feedItems,
    feedSources,
    insights,
    savedItems,
    skills,
    betaOpportunities,
    companyIntelligence,
    modelComparisons,
    loading,
    error,
    isMalcolm,
    
    // Filters
    activeFilter,
    setActiveFilter,
    searchQuery,
    setSearchQuery,
    sortBy,
    setSortBy,
    
    // Actions
    loadFeedItems,
    loadInsights,
    loadSavedItems,
    saveToWorkspace,
    updateSkillLevel,
    dismissInsight,
    deleteFeedItem,
    
    // Refresh functions
    refreshAll: async () => {
      await Promise.all([
        loadFeedItems(),
        loadInsights(),
        loadSavedItems(),
        loadSkills(),
        loadBetaOpportunities(),
        loadCompanyIntelligence(),
        loadModelComparisons()
      ]);
    }
  };

  return (
    <MalcolmContext.Provider value={value}>
      {children}
    </MalcolmContext.Provider>
  );
};